"use client";

import { c as _c } from "react/compiler-runtime";
import { Area, AreaChart, Bar, BarChart, Line, LineChart, ResponsiveContainer } from "recharts";
import type React from "react";
import type { ReferenceRange } from "@/types/measurement";
import cartesianElements from "./cartesian-elements";
import type { MeasurementChartPayloadItem, MeasurementChartPointSource } from "./chart-tooltip";
export interface MeasurementSingleChartDatum {
  date: string;
  value: number | null;
  isUserReported?: boolean;
}
export interface MeasurementSingleChartProps {
  chartType?: "line" | "bar" | "area";
  data: MeasurementSingleChartDatum[];
  fieldName: string;
  color: string;
  unit?: string | null;
  /** Recharts annotation elements (reference areas, lines, goals) */
  annotations?: React.ReactNode[];
  /** Override cartesian element options */
  cartesianOpts?: {
    tickFontSize?: number;
    yAxisWidth?: number;
    tooltipSize?: "sm" | "xs";
    formatDate?: (date: string, style: "short" | "long") => string;
    referenceRanges?: ReferenceRange[];
  };
  /** When true, uses faster animation (600ms). When false, no animation props. */
  animated?: boolean;
}
function getSinglePointSource(item: MeasurementChartPayloadItem): MeasurementChartPointSource | null {
  const isUserReported = item.payload?.isUserReported;
  return typeof isUserReported === "boolean" ? {
    isUserReported
  } : null;
}
export default function MeasurementSingleChart(t0) {
  const $ = _c(41);
  const {
    chartType: t1,
    data,
    fieldName,
    color,
    unit,
    annotations,
    cartesianOpts,
    animated: t2
  } = t0;
  const chartType = t1 === undefined ? "line" : t1;
  const animated = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      top: 8,
      right: 8,
      left: 0,
      bottom: 8
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const margin = t3;
  let t4;
  if ($[1] !== animated) {
    t4 = animated ? {
      animationDuration: 600
    } : {};
    $[1] = animated;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const animProps = t4;
  const yAxisUnit = unit ? ` ${unit}` : undefined;
  let t5;
  if ($[3] !== cartesianOpts || $[4] !== fieldName || $[5] !== unit || $[6] !== yAxisUnit) {
    t5 = cartesianElements({
      fieldName,
      unit,
      yAxisUnit,
      getPointSource: getSinglePointSource,
      ...cartesianOpts
    });
    $[3] = cartesianOpts;
    $[4] = fieldName;
    $[5] = unit;
    $[6] = yAxisUnit;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const shared = t5;
  let t6;
  if ($[8] !== annotations) {
    t6 = annotations ?? [];
    $[8] = annotations;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const annotationNodes = t6;
  if (chartType === "bar") {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = [2, 2, 0, 0];
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== animProps || $[12] !== color || $[13] !== fieldName) {
      t8 = <Bar dataKey="value" fill={color} fillOpacity={0.85} radius={t7} name={fieldName} {...animProps} />;
      $[11] = animProps;
      $[12] = color;
      $[13] = fieldName;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== annotationNodes || $[16] !== data || $[17] !== shared || $[18] !== t8) {
      t9 = <ResponsiveContainer width="100%" height="100%"><BarChart data={data} margin={margin}>{shared}{annotationNodes}{t8}</BarChart></ResponsiveContainer>;
      $[15] = annotationNodes;
      $[16] = data;
      $[17] = shared;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    return t9;
  }
  if (chartType === "area") {
    let t7;
    if ($[20] !== animProps || $[21] !== color || $[22] !== fieldName) {
      t7 = <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} name={fieldName} {...animProps} />;
      $[20] = animProps;
      $[21] = color;
      $[22] = fieldName;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] !== annotationNodes || $[25] !== data || $[26] !== shared || $[27] !== t7) {
      t8 = <ResponsiveContainer width="100%" height="100%"><AreaChart data={data} margin={margin}>{shared}{annotationNodes}{t7}</AreaChart></ResponsiveContainer>;
      $[24] = annotationNodes;
      $[25] = data;
      $[26] = shared;
      $[27] = t7;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  let t7;
  if ($[29] !== color) {
    t7 = {
      r: 3,
      fill: color
    };
    $[29] = color;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== animProps || $[32] !== color || $[33] !== fieldName || $[34] !== t7) {
    t8 = <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={t7} name={fieldName} {...animProps} />;
    $[31] = animProps;
    $[32] = color;
    $[33] = fieldName;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== annotationNodes || $[37] !== data || $[38] !== shared || $[39] !== t8) {
    t9 = <ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={margin}>{shared}{annotationNodes}{t8}</LineChart></ResponsiveContainer>;
    $[36] = annotationNodes;
    $[37] = data;
    $[38] = shared;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
}